import { defineStore } from "pinia";
import * as fb from "../firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

export interface ProfilePermissions {
  profileId: string;
  active?: boolean;
  data?: string[];
}

interface ProfilePermissionsState {
  profiles: ProfilePermissions[];
  loading: boolean;
}

export const useProfilePermissionsStore = defineStore("profilePermissions", {
  state: (): ProfilePermissionsState => ({
    profiles: [],
    loading: false,
  }),

  actions: {
    async fetchAllProfiles(): Promise<ProfilePermissions[]> {
      this.loading = true;
      try {
        const _permissions = collection(fb.firestore, "permissions");
        const profiles = await getDocs(_permissions);

        if (profiles.size > 0) {
          const confs: ProfilePermissions[] = [];
          profiles.forEach((d) => {
            const s = d.data();
            confs.push({
              profileId: d.id,
              active: s.active !== undefined ? s.active : true,
              data: s.data || [],
            });
          });
          this.profiles = confs;
          return confs;
        } else {
          // No profiles configured: treat as a valid empty state
          this.profiles = [];
          return [];
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addProfile(request: ProfilePermissions): Promise<{
      OPCODE: string;
      message: string;
      errorDetail?: any;
    }> {
      try {
        await setDoc(doc(fb.firestore, "permissions", request.profileId), {
          active: request.active !== undefined ? request.active : true,
          data: request.data || [],
        });

        // Refresh profiles after adding
        await this.fetchAllProfiles();

        return {
          OPCODE: "SUCCESS",
          message: "Successfully added profile in collection",
        };
      } catch (error: any) {
        return {
          OPCODE: "ERROR",
          message: error.message,
          errorDetail: error,
        };
      }
    },

    async updateProfile(request: ProfilePermissions): Promise<{
      OPCODE: string;
      message: string;
      errorDetail?: any;
    }> {
      try {
        await updateDoc(doc(fb.firestore, "permissions", request.profileId), {
          active: request.active !== undefined ? request.active : true,
          data: request.data || [],
        });

        // Refresh profiles after updating
        await this.fetchAllProfiles();

        return {
          OPCODE: "SUCCESS",
          message: "Successfully updated profile in collection",
        };
      } catch (error: any) {
        return {
          OPCODE: "ERROR",
          message: error.message,
          errorDetail: error,
        };
      }
    },

    async updateMultipleProfiles(profiles: ProfilePermissions[]): Promise<{
      OPCODE: string;
      message: string;
      errorDetail?: any;
    }> {
      try {
        // Use batch writes for better performance and atomicity
        const batch = writeBatch(fb.firestore);

        profiles.forEach((profile) => {
          const docRef = doc(fb.firestore, "permissions", profile.profileId);
          batch.update(docRef, {
            active: profile.active !== undefined ? profile.active : true,
            data: profile.data || [],
          });
        });

        await batch.commit();

        // Refresh profiles after batch update
        await this.fetchAllProfiles();

        return {
          OPCODE: "SUCCESS",
          message: "Successfully updated all profiles",
        };
      } catch (error: any) {
        return {
          OPCODE: "ERROR",
          message: error.message,
          errorDetail: error,
        };
      }
    },
  },

  getters: {
    getProfileById: (state) => {
      return (profileId: string): ProfilePermissions | undefined => {
        return state.profiles.find((p) => p.profileId === profileId);
      };
    },

    activeProfiles: (state): ProfilePermissions[] => {
      return state.profiles.filter((p) => p.active !== false);
    },

    allProfiles: (state): ProfilePermissions[] => {
      return state.profiles;
    },
  },
});
